import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, TextareaField, SelectField, SwitchField } from "@/components/kit/fields";
import { resolveAsset } from "@/components/kit/FileUpload";

const ORG_CURRENCIES = ["USD", "EUR", "GBP", "UAH", "PLN", "CZK", "RON", "KZT", "TRY", "AED", "BRL", "INR", "CAD", "AUD", "JPY", "CHF", "SEK", "NOK", "DKK", "MXN"];

const empty = {
  name: "", slug: "", plan: "Growth", plan_id: "", trial: false, status: "active",
  defaultCurrency: "USD", notes: "", owner_name: "", owner_email: "", owner_password: "",
  allow_portal_domain: false,
};

function InfoRow({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-white/[0.05] py-1.5 text-sm last:border-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="max-w-[60%] truncate text-right text-foreground/90">{v || "—"}</span>
    </div>
  );
}

export function OrganizationDrawer({ open, item, orgInfo, branding, onOpenChange }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(empty);
  const isEdit = !!item?.internalId;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: billing } = useQuery({
    queryKey: ["billing-settings"], enabled: open && !isEdit,
    queryFn: async () => (await http.get("/billing/settings")).data,
  });
  const planChoices = (billing?.plans || []).filter((p) => p.active);
  const trialDays = billing?.free_trial_days || 14;

  useEffect(() => {
    if (open) setForm(item ? { ...empty, ...item } : empty);
  }, [open, item]);

  useEffect(() => {
    if (open && !isEdit && !form.plan_id && planChoices.length) {
      const firstPaid = planChoices.find((p) => p.period !== "trial") || planChoices[0];
      set("plan_id", firstPaid.id);
    }
  }, [open, isEdit, planChoices, form.plan_id]);

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit) return (await http.put(`/tenants/${item.internalId}`, form)).data;
      return (await http.post("/tenants", form)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(isEdit ? "Platform settings updated" : "Organization created");
      onOpenChange(false);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const onSave = () => {
    if (!isEdit && (!form.name || !form.slug)) return toast.error("Name and slug are required");
    if (!isEdit && !form.owner_email) return toast.error("Tenant Admin email is required");
    save.mutate();
  };

  const oi = orgInfo || {};
  const logo = resolveAsset(branding?.logo_url);

  return (
    <FormDrawer open={open} onOpenChange={onOpenChange} title={isEdit ? "Edit Organization" : "New Organization"}
      subtitle={isEdit ? (oi.partner_network_name || form.name) : "Create a tenant and its admin"} size="lg"
      onSave={onSave} saving={save.isPending} saveLabel={isEdit ? "Save platform settings" : "Create organization"} testId="organization-drawer">
      <div className="space-y-6">
        {/* PLATFORM SETTINGS — managed by Super Admin */}
        <div className="space-y-4 rounded-lg border border-white/[0.08] bg-white/[0.02] p-4" data-testid="org-platform-settings">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">Platform settings</p>
          {!isEdit && (
            <div className="grid grid-cols-2 gap-4">
              <TextField label="Organization name" value={form.name} onChange={(v) => set("name", v)} testId="org-name" />
              <TextField label="Slug" value={form.slug} onChange={(v) => set("slug", v)} testId="org-slug" hint="Used for tenant id" />
            </div>
          )}
          {!isEdit ? (
            <>
              <SelectField label="Plan" value={form.plan_id} onChange={(v) => set("plan_id", v)}
                options={planChoices.map((p) => ({
                  value: p.id,
                  label: p.period === "trial" ? `${p.name} · ${trialDays}-day trial (free)` : `${p.name} · ${p.price} ${p.currency}`,
                }))}
                placeholder="Select a plan" testId="org-plan" hint="Trial is a full plan here — pick it to start the tenant without a paid subscription." />
              <div className="grid grid-cols-2 gap-4">
                <SelectField label="Organization currency" value={form.defaultCurrency} onChange={(v) => set("defaultCurrency", v)} options={ORG_CURRENCIES} testId="org-currency" />
              </div>
              <p className="-mt-2 text-xs text-[#EAB308]">Chosen once — the whole network (statistics, payouts, finance) runs in this currency. It cannot be changed after creation.</p>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <SelectField label="Plan" value={form.plan} onChange={(v) => set("plan", v)}
                options={[...new Set([form.plan, "Starter", "Growth", "Enterprise"].filter(Boolean))]} testId="org-plan" />
              <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={["active", "suspended"]} testId="org-status" />
              <div data-testid="org-currency-readonly">
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">Organization currency (immutable)</div>
                <div className="flex h-10 items-center rounded-md border border-white/[0.08] bg-white/[0.03] px-3 text-sm text-foreground/80">{form.defaultCurrency || "USD"}</div>
                <p className="mt-1 text-xs text-muted-foreground/70">Set at creation. Cannot be changed — statistics, payouts and finance all rely on it.</p>
              </div>
            </div>
          )}
          <TextareaField label="Internal notes (private to Super Admin)" value={form.notes} onChange={(v) => set("notes", v)} testId="org-notes" />
          <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3">
            <SwitchField label="Allow custom Partner Portal domain"
              hint="When off, this tenant can only connect custom domains for Tracking and Postbacks — the affiliate portal always runs on the platform domain. When on, they may fully white-label the partner portal on their own domain."
              checked={!!form.allow_portal_domain} onChange={(v) => set("allow_portal_domain", v)} testId="org-allow-portal-domain" />
          </div>
        </div>

        {!isEdit && (
          <div className="space-y-4 rounded-lg border border-white/[0.08] bg-white/[0.02] p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">Tenant Admin (owner)</p>
            <TextField label="Admin name" value={form.owner_name} onChange={(v) => set("owner_name", v)} testId="org-owner-name" />
            <div className="grid grid-cols-2 gap-4">
              <TextField label="Admin email" type="email" value={form.owner_email} onChange={(v) => set("owner_email", v)} testId="org-owner-email" />
              <TextField label="Admin password" type="password" value={form.owner_password} onChange={(v) => set("owner_password", v)} testId="org-owner-password" />
            </div>
          </div>
        )}

        {/* ORGANIZATION INFORMATION — read-only, owned by the tenant (single source of truth) */}
        {isEdit && (
          <div className="space-y-3 rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-4" data-testid="org-info-readonly">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">Organization information</p>
              <span className="text-[11px] text-muted-foreground/60">read-only · managed by the tenant</span>
            </div>
            {logo && <img src={logo} alt="logo" className="h-10 w-auto rounded bg-white/[0.04] object-contain p-1" />}
            <div>
              <InfoRow k="Partner network name" v={oi.partner_network_name} />
              <InfoRow k="Legal name" v={oi.legal_name} />
              <InfoRow k="Website" v={oi.website} />
              <InfoRow k="Contact email" v={oi.contact_email} />
              <InfoRow k="Support email" v={oi.support_email} />
              <InfoRow k="Country" v={oi.country} />
              <InfoRow k="Telegram" v={oi.telegram} />
              <InfoRow k="WhatsApp" v={oi.whatsapp} />
              <InfoRow k="Phone" v={oi.phone} />
              <InfoRow k="Org currency" v={item?.defaultCurrency || oi.default_currency} />
              <InfoRow k="Org timezone" v={oi.timezone} />
              <InfoRow k="Language" v={oi.default_language} />
            </div>
          </div>
        )}
      </div>
    </FormDrawer>
  );
}
